import { Search, ChevronDown, User, Settings, LogOut, Building2, Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganisation, ORGANISATION_ROLE_LABELS } from '@/contexts/OrganisationContext';
import meddLogo from '@/assets/medd-logo-white.png';
import { UnifiedNotificationBell } from '@/components/notifications/UnifiedNotificationBell';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface HeaderProps {
  onOpenNavigation?: () => void;
  pageTitle?: string;
}

const routeTitleMap: Record<string, string> = {
  '/': 'Dashboard',
  '/simulations': 'My Simulations',
  '/my-sessions': 'My Sessions',
  '/progress': 'Progress',
  '/leaderboards': 'Leaderboards',
  '/learning/pathways': 'Learning Pathways',
  '/coaching/history': 'Coaching History',
  '/coaching/debrief': 'Debrief Session',
  '/settings': 'Settings',
  '/help/knowledge-base': 'Knowledge Base',
  '/connection-check': 'Connection Check',
  '/content-studio': 'Content Studio',
  '/admin': 'Admin Dashboard',
  '/admin/users': 'User Management',
  '/admin/agent-templates': 'Agent Templates',
  '/admin/agents': 'Agent Management',
  '/admin/companies': 'Company Management',
  '/admin/organisation': 'Organisation',
  '/admin/pathways': 'Pathway Management',
  '/admin/certificates': 'Certificate Management',
  '/admin/assessments': 'Physical Assessments',
  '/admin/coaching-templates': 'Coaching Templates',
  '/admin/rubric-templates': 'Rubric Templates',
  '/admin/system-config': 'System Configuration',
  '/company-dashboard': 'Company Dashboard',
};

function getPageTitle(pathname: string): string {
  // Exact match first
  if (routeTitleMap[pathname]) return routeTitleMap[pathname];
  
  // Pattern matches for dynamic routes
  if (pathname.startsWith('/simulation/') && pathname.endsWith('/video')) return 'Video Simulation';
  if (pathname.startsWith('/simulation/results/')) return 'Simulation Results';
  if (pathname.startsWith('/simulation/')) return 'Simulation Details';
  if (pathname.startsWith('/coaching/results/')) return 'Coaching Results';
  if (pathname.startsWith('/coaching/') && pathname.endsWith('/session')) return 'Coaching Session';
  if (pathname.startsWith('/learning/pathway/')) return 'Learning Pathway';
  if (pathname.startsWith('/learning/quiz/')) return 'Quiz Module';
  if (pathname.startsWith('/learning/reading/')) return 'Reading Module';
  if (pathname.startsWith('/admin/users/')) return 'User Management';
  if (pathname.startsWith('/admin/agents/')) return 'Agent Management';
  if (pathname.startsWith('/admin/companies/')) return 'Company Management';
  if (pathname.startsWith('/admin/coaching-templates/')) return 'Coaching Templates';
  if (pathname.startsWith('/admin/rubric-templates/')) return 'Rubric Templates';
  
  return 'Dashboard';
}

export function Header({ onOpenNavigation, pageTitle }: HeaderProps) {
  const location = useLocation();
  const dynamicTitle = pageTitle || getPageTitle(location.pathname);
  const { user, logout } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { units, activeUnitId, activeUnit, effectiveRole, setActiveUnit } = useOrganisation();
  const navigate = useNavigate();
  
  // The header renders tenant branding on a light surface.
  const hasTenantLogo = tenant.logo_dark_url || tenant.logo_url;
  const headerLogo = !isMasterTenant && hasTenantLogo 
    ? (tenant.logo_url || tenant.logo_dark_url)
    : (!isMasterTenant ? null : meddLogo); // White-label without logo shows nothing, not MEDD
  const activeUnitLabel = activeUnit
    ? `${activeUnit.branch_name} · ${activeUnit.name}`
    : 'Select business unit';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleDisplay = () => {
    if (!isMasterTenant && effectiveRole) return ORGANISATION_ROLE_LABELS[effectiveRole];
    if (!user || !user.roles.length) return 'User';
    
    const roleMap: Record<string, string> = {
      admin: isMasterTenant ? 'System Administrator' : 'Tenant Administrator',
      company_admin: 'Company Administrator',
      sales_rep: 'Medical Sales Representative',
      healthcare_professional: 'Healthcare Professional',
      user: 'User',
    };
    
    // Show the first recognized role
    const firstRole = user.roles.find(r => roleMap[r]);
    return firstRole ? roleMap[firstRole] : user.roles[0];
  };

  return (
    <header className="sticky top-0 z-50 h-16 min-w-0 bg-white border-b border-border shadow-sm">
      <div className="flex h-full min-w-0 items-center gap-3 px-4 xl:px-6">
        {onOpenNavigation && (
          <button
            type="button"
            onClick={onOpenNavigation}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        {/* Left side - Logo and Title */}
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div
            className={cn(
              'hidden h-10 w-40 min-w-0 items-center justify-center overflow-hidden rounded-md px-2 2xl:flex',
              isMasterTenant ? 'bg-primary' : 'border bg-white',
            )}
          >
            {headerLogo ? (
              <img 
                src={headerLogo} 
                alt={`${tenant.tenant_name} Logo`}
                className={cn(
                  'h-full w-full object-contain',
                  !isMasterTenant && 'scale-[1.65]',
                )}
                onError={(e) => {
                  if (isMasterTenant) {
                    (e.target as HTMLImageElement).src = meddLogo;
                  } else {
                    const image = e.target as HTMLImageElement;
                    image.style.display = 'none';
                    image.nextElementSibling?.classList.remove('hidden');
                  }
                }}
              />
            ) : null}
            <span className={cn(
              'max-w-36 truncate text-sm font-semibold',
              headerLogo && 'hidden',
              isMasterTenant ? 'text-white' : 'text-primary',
            )}>
              {tenant.tenant_name || 'Training Platform'}
            </span>
          </div>
          <h1 className="min-w-0 truncate text-xl font-bold text-foreground">{dynamicTitle}</h1>
        </div>

        {!isMasterTenant && units.length > 0 && (
          <div className="hidden min-w-0 flex-none justify-center px-2 xl:flex">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex w-52 min-w-0 items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/60 2xl:w-64">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="min-w-0 flex-1 truncate text-left">{activeUnitLabel}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-72">
                <DropdownMenuLabel>Working context</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {units.map((unit) => (
                  <DropdownMenuItem
                    key={unit.id}
                    onClick={() => setActiveUnit(unit.id)}
                    className={unit.id === activeUnitId ? 'bg-muted' : undefined}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{unit.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{unit.branch_name}</span>
                    </span>
                    {unit.id === activeUnitId && <span className="text-xs text-primary">Active</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Right side - Actions and User */}
        <div className="flex shrink-0 items-center gap-2 xl:gap-3">
          {/* Search Icon */}
          <button className="hidden p-2 transition-colors hover:bg-muted rounded-lg sm:inline-flex">
            <Search className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* Tenant/Company Badge - Show tenant name for admins, company name for others */}
          {(tenant?.tenant_name || user?.company_name) && (
            <div className="hidden max-w-56 min-w-0 items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 px-3 py-1.5 2xl:flex">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="truncate text-sm font-medium text-primary">
                {user?.roles?.includes('admin') ? tenant?.tenant_name : user?.company_name}
              </span>
            </div>
          )}

          {/* Unified Notifications */}
          <UnifiedNotificationBell />

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg transition-colors">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white font-semibold text-sm overflow-hidden">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    user && getInitials(user.name)
                  )}
                </div>
                <div className="hidden max-w-48 min-w-0 text-left 2xl:block">
                  <div className="truncate text-sm font-semibold text-foreground">{user?.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {getRoleDisplay()}
                  </div>
                </div>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground 2xl:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  {user?.company_name && (
                    <p className="text-xs text-muted-foreground">{user.company_name}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => navigate('/settings?section=profile')}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => navigate('/settings')}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
